import { useTranslations } from '@vocab/react';
import { Box, Stack, TextLink, useToast } from 'braid-design-system';
import { observer } from 'mobx-react';
import { type FC, useState, useEffect } from 'react';
import loadable from 'sku/@loadable/component';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';

import AppErrorBoundary from 'src/components/app/AppErrorBoundary';
import { useFilters } from 'src/components/app/FilterSideBar/useFilters';
import JobSection from 'src/components/app/JobSection';
import {
  UserActionStatusProvider,
  useUserActionStatus,
} from 'src/components/common/UserActionStatusContext';
import CandidateList from 'src/components/list/index';
import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { HOTJAR_TAG_BULK_ACTIONS, HOTJAR_TAG_SORT } from 'src/constants/hotjar';
import { getDashisedBucket } from 'src/constants/jobApplicationStatus';
import { BULK_MOVE_LIMIT } from 'src/constants/numPerPage';
import { Display } from 'src/libs/Display';
import type { PageConfig } from 'src/libs/PageConfig';
import { IntercomConfiguration } from 'src/modules/intercom/IntercomConfiguration';
import {
  PAGE_QUERY_PARAM_KEY,
  PAGE_TAB_PARAM_KEY,
} from 'src/pages/CandidateManagementPage/queryParams';
import type { BucketInfo } from 'src/stores/BucketStatsStore';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import { SUPPORT_BACK_BUTTON } from 'src/utils/buildHandleEnterAndSpaceKeyPress';
import {
  trackBulkActionsEmailTabClicked,
  trackBulkActionsMoveTabClicked,
  trackCandidateCardStatus,
  trackClickBillableApplicantsDefinition,
  trackFreemiumButtonDisplay,
  trackFreemiumButtonPressedEvent,
  trackSortOptionChangedEvent,
  trackUpdateStatusMultiple,
} from 'src/utils/events';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';
import { useStores } from 'src/utils/hooks/useStores';
import { sendHotjarTag } from 'src/utils/hotjar';
import { isANZSite } from 'src/utils/site';
import { sleep } from 'src/utils/sleep';

import { MobileFilterSection } from '../../components/app/MobileFilterSection';
import UnhandledErrorPageBlock from '../../components/app/UnhandledErrorPageBlock';
import { getSignInUrl } from '../../pages/CandidateManagementPage/utils/getSignInUrl';
import type { IJobStore } from '../../stores/JobStore';
import type { IQuestionnaireStore } from '../../stores/JobStore/QuestionnaireStore';
import { UserLoadState } from '../../stores/UserStore/types';
import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../utils/getCsContactMethodMessageFromSite';
import { useExperimentConfig } from '../CandidateManagementPage/ExperimentContext';
import { JobPlacementDialog } from '../CandidateManagementPage/JobPlacementDialog';
import { type PageTab, PageTabs } from '../CandidateManagementPage/PageTabs';
import { RelevantApplicationDialog } from '../CandidateManagementPage/RelevantApplicationDialog';
import { JobPlacementAlert } from '../JobApplicationListSection/JobPlacementAlert';
import { shouldShowRecruiterNetwork } from '../JobApplicationListSection/RecruiterNetworkAlert';
import useHandleStatusChange from '../hooks/useHandleStatusChange';

import translations from './.vocab';
import { ActionToolbar } from './ActionToolbar';
import { useApplicationsFacetsQuery } from './AppPageBlock.hirer.generated';
import { AppPageLayout } from './AppPageLayout';
import { FilterDesktop } from './FilterDesktop';
import { FilterDrawerMobile } from './FilterDrawerMobile';
import { FilterLocation } from './FilterLocation';
import { FilterNationality } from './FilterNationality';
import { FilterScreeningQuestion } from './FilterScreeningQuestion';
import { StatusFolderButton } from './FilterStatusFolders/StatusFolderButton';
import { FilterTabletDesktopContentLayout } from './FilterTabletDesktopContentLayout';
import { FreemiumButton } from './FreemiumButton';
import {
  FreemiumHardToFillModal,
  FreemiumUrgentHireModal,
} from './FreemiumModal';
import { MobileStickyHeader } from './MobileStickyHeader';
import NotificationPreferenceDialog from './NotificationPreferenceDialog/NotificationPreferenceDialog';
import PageAlert from './PageAlert';
import { SearchResultSummary } from './SearchResultSummary';
import type { CandidatesManagementPageAnalytics } from './createPageAnalytics';
import type {
  ApplicationStatusFolder,
  QuestionnaireFilter,
} from './graphql/hirer-types.generated';

import * as pageBlockStyles from '../../components/layouts/PageBlock/PageBlock.css';

const HirerJobActivity = loadable(
  () => import('src/components/app/HirerActivity/HirerJobActivity'),
);

const isPageTab = (value: unknown): value is PageTab =>
  value === 'applications' || value === 'notes';

export interface CandidateManagementAppProps {
  employerHeader: JSX.Element;
  pageAnalytics: CandidatesManagementPageAnalytics;
  initialLoadResponse:
    | { ok: false }
    | {
        ok: true;
        jobId: number;
        canBeUpgraded: boolean;
        canShowHomeLocationFilterInCandidateManagement: boolean;
        canShowNationalityFilterInCandidateManagement: boolean;
        canShowJobPlacement: boolean;
      };
  trackingExperiments:
    | Array<{
        num: number;
        id: string;
        variant: string;
      }>
    | undefined;
  dataDogRum: PageConfig['dataDogRum'];
}

export const CandidateManagementApp: FC<CandidateManagementAppProps> = observer(
  ({
    employerHeader,
    initialLoadResponse,
    pageAnalytics,
    trackingExperiments,
    dataDogRum,
  }) => {
    const { t } = useTranslations(translations);
    const stores = useStores();
    const userStore = stores.userStore;
    const jobStore: IJobStore = stores.jobStore;
    const { site, language } = useAppConfig();

    if (userStore.userLoadState === UserLoadState.UNAUTHENTICATED) {
      return (
        <PageBlock>
          <PageAlert>
            {t('Sign in to see this page.', {
              Link: (children) => (
                <TextLink
                  key={UserLoadState.UNAUTHENTICATED}
                  href={getSignInUrl()}
                >
                  {children}
                </TextLink>
              ),
            })}
          </PageAlert>
        </PageBlock>
      );
    }

    if (userStore.userLoadState === UserLoadState.FORBIDDEN) {
      return (
        <PageBlock>
          <PageAlert>
            {t(
              "This account can't access this page. Would you like to switch accounts?",
              {
                Link: (children) => (
                  <TextLink key={UserLoadState.FORBIDDEN} href={getSignInUrl()}>
                    {children}
                  </TextLink>
                ),
              },
            )}
          </PageAlert>
        </PageBlock>
      );
    }

    if (!jobStore.hasAccess) {
      const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);

      const noAccessToJobMessage = isANZSite(site)
        ? t(
            'If you think you should be one of them, ask your system administrator...',
            {
              TelLink: () => (
                <TextLink href={`tel:${csPhoneNumber}`}>
                  {csPhoneNumber}
                </TextLink>
              ),
            },
          )
        : t(
            'If you think you should be one of them, ask your system administrator - with Customer Service link',
            {
              Link: (children) => (
                <TextLink href={getHelpCentreUrlFromSite(site, language)}>
                  {children}
                </TextLink>
              ),
            },
          );
      return (
        <PageBlock>
          <PageAlert>
            {t('Only some people can see this page')}
            <br />
            {noAccessToJobMessage}
          </PageAlert>
        </PageBlock>
      );
    }

    if (
      jobStore.isError ||
      userStore.userLoadState === UserLoadState.ERROR ||
      // Note: initialLoadResponse.ok (React scope) could be the same as jobStore.isError (MobX)
      // We should look to remove the MobX version if possible
      !initialLoadResponse.ok
    ) {
      return (
        <PageBlock>
          <Box marginY="small" marginX="gutter">
            <UnhandledErrorPageBlock />
          </Box>
        </PageBlock>
      );
    }

    return (
      <UserActionStatusProvider
        jobId={`${initialLoadResponse.jobId}`}
        userId={stores.userStore.user.userId || 0}
      >
        <AppPageBlock
          employerHeader={employerHeader}
          jobId={initialLoadResponse.jobId}
          canShowHomeLocationFilterInCandidateManagement={
            initialLoadResponse.canShowHomeLocationFilterInCandidateManagement
          }
          canBeUpgraded={initialLoadResponse.canBeUpgraded}
          canShowNationalityFilterInCandidateManagement={
            initialLoadResponse.canShowNationalityFilterInCandidateManagement
          }
          canShowJobPlacement={initialLoadResponse.canShowJobPlacement}
          pageAnalytics={pageAnalytics}
          trackingExperiments={trackingExperiments}
          dataDogRum={dataDogRum}
        />
      </UserActionStatusProvider>
    );
  },
);

interface AppPageBlockProps {
  employerHeader: JSX.Element;
  jobId: number;
  canShowHomeLocationFilterInCandidateManagement: boolean;
  canBeUpgraded: boolean;
  canShowNationalityFilterInCandidateManagement: boolean;
  canShowJobPlacement: boolean;
  pageAnalytics: CandidatesManagementPageAnalytics;
  trackingExperiments:
    | Array<{
        num: number;
        id: string;
        variant: string;
      }>
    | undefined;
  dataDogRum: PageConfig['dataDogRum'];
}

const AppPageBlock: FC<AppPageBlockProps> = observer((props) => {
  const {
    employerHeader,
    jobId,
    canBeUpgraded,
    canShowHomeLocationFilterInCandidateManagement,
    canShowNationalityFilterInCandidateManagement,
    canShowJobPlacement,
    pageAnalytics,
    trackingExperiments,
    dataDogRum,
  } = props;
  const store = useStores();
  const { seekAdvertiserId, jobApplicationStore, userStore, isMobile } = store;
  const jobStore: IJobStore = store.jobStore;
  const questionStore: IQuestionnaireStore = jobStore.questionnaire;

  const { language, router } = useAppConfig();
  const [isFilterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const { t } = useTranslations(translations);
  const showToast = useToast();
  const { isExperimentEnabled } = useExperimentConfig();

  const { getJobPlacementExpiryTimestamp, hideJobPlacementExpiryTimestamp } =
    useUserActionStatus('job-placement-expiry-timestamp');
  const { getFilterDrawerState, toggleFilterDrawerState } = useUserActionStatus(
    'filter-drawer-state',
  );

  const [isJobPlacementShown, setIsJobPlacementShown] = useState(() => {
    if (
      !canShowJobPlacement ||
      shouldShowRecruiterNetwork(
        jobStore.job.jobInformation.jobCreatedDate,
        jobStore.job.jobInformation.categoryId,
        jobStore.job.jobInformation.locationIds,
      )
    ) {
      return {
        nudge: false,
        dialog: false,
      };
    }
    const jobCreatedDate = new Date(jobStore.job.jobInformation.jobCreatedDate);
    if (isNaN(jobCreatedDate.getTime())) {
      return {
        nudge: false,
        dialog: false,
      };
    }
    const currentDate = new Date();
    const bufferDateString = getJobPlacementExpiryTimestamp();
    if (bufferDateString) {
      const bufferDate = new Date(bufferDateString);
      if (!isNaN(bufferDate.getTime()) && currentDate <= bufferDate) {
        return {
          nudge: false,
          dialog: false,
        };
      }
    }
    if (isExperimentEnabled('growth-placement-passive-flow')) {
      return {
        dialog: false,
        nudge: true,
      };
    }
    const differenceInTime =
      currentDate.getTime() - new Date(jobCreatedDate).getTime();
    const diffDays = Math.floor(differenceInTime / (1000 * 3600 * 24));
    if (
      (isExperimentEnabled('growth-placement-assertive-flow-25-days') &&
        diffDays >= 25) ||
      (isExperimentEnabled('growth-placement-assertive-flow-10-days') &&
        diffDays >= 10)
    ) {
      return {
        dialog: true,
        nudge: false,
      };
    }
    return {
      nudge: false,
      dialog: false,
    };
  });

  const questions = jobApplicationStore.filtersStore.questions;

  const { filters, filterFns: useFiltersFns } = useFilters({
    language,
    trackingExperiments,
    addCustomError: dataDogRum.addCustomError,
  });

  const [isAtTopOfPage, setIsAtTopOfPage] = useState(true);
  const [, setPageQueryString] = useQueryParam(
    PAGE_QUERY_PARAM_KEY,
    NumberParam,
  );
  const [selectedPageTabUntyped, setPageTabString] = useQueryParam(
    PAGE_TAB_PARAM_KEY,
    StringParam,
  );
  const [queryParams, setQueryParams] = useQueryParamsStore();
  const [isStickyOnDesktop, setIsStickyOnDesktop] = useState(false);
  const [isRelevantApplicationDialogOpen, setIsRelevantApplicationDialogOpen] =
    useState<boolean>(false);

  useEffect(() => {
    store.jobApplicationStore.updateFromQuery({
      queryParams: {
        jobId: queryParams.jobId,
        positionId: queryParams.positionId,
        bucket: queryParams.bucket,
        page: queryParams.page,
        selectedProspectId: queryParams.selectedProspectId,
        search: queryParams.search,
        overlay: queryParams.overlay,
        referrer: queryParams.referrer,
        tab: queryParams.tab,
      },
      language,
      initialLoad: false,
      filtersEventCaptureData: {
        filtered_apps_in_bucket:
          filters.filtersEventCaptureData.filtered_apps_in_bucket,
        filters_applied_to_bucket:
          filters.filtersEventCaptureData.filters_applied_to_bucket,
        total_apps_in_bucket:
          filters.filtersEventCaptureData.total_apps_in_bucket,
      },
      filters: filters.searchFilters,
      experiments: trackingExperiments,
      addCustomError: dataDogRum.addCustomError,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryParams.jobId,
    queryParams.positionId,
    queryParams.bucket,
    queryParams.page,
    queryParams.selectedProspectId,
    queryParams.search,
    queryParams.overlay,
    queryParams.referrer,
    queryParams.tab,
    store,
    language,
    filters.filtersEventCaptureData.filtered_apps_in_bucket,
    filters.filtersEventCaptureData.filters_applied_to_bucket,
    filters.filtersEventCaptureData.total_apps_in_bucket,
    filters.searchFilters,
  ]);

  const setSelectedPageTab = (tab: PageTab) => {
    switch (tab) {
      case 'notes':
        setPageTabString('notes');
        break;
      default:
        setPageTabString(undefined);
    }
  };
  const selectedPageTab = isPageTab(selectedPageTabUntyped)
    ? selectedPageTabUntyped
    : 'applications';

  const shouldShowRecommendedCandidates = jobStore.job.isActive;

  const openRelevantApplicationAlertFromBanner = () => {
    trackClickBillableApplicantsDefinition({
      actionOrigin: 'Candidate Listing - Billable Alert',
    });
    setIsRelevantApplicationDialogOpen(true);
  };

  const shouldShowFreemium = userStore.user.canCreateJobs && canBeUpgraded;

  const canShowLocationFilter = canShowHomeLocationFilterInCandidateManagement;

  const canShowNationalityFilter =
    canShowNationalityFilterInCandidateManagement;

  const hidePageTabs =
    isMobile && (!shouldShowRecommendedCandidates || !isAtTopOfPage);

  const isMustHaveToggleSelected =
    jobApplicationStore.filtersStore.isMustHaveToggleSelected;

  const onSortByChange: (params: { sortBy: string }) => Promise<void> = async ({
    sortBy,
  }) => {
    const defaultPage = 1;

    trackSortOptionChangedEvent({ sortMode: sortBy });
    sendHotjarTag(HOTJAR_TAG_SORT);

    await jobApplicationStore.loadJobApplications({
      sortBy,
      page: defaultPage,
      language,
      filters: filters.searchFilters,
      experiments: trackingExperiments,
      addCustomError: dataDogRum.addCustomError,
    });
    setPageQueryString(defaultPage, SUPPORT_BACK_BUTTON);
  };

  const jobPostingCountry = jobStore.job.jobInformation.jobCountry;

  const applicationFacetsResult = useApplicationsFacetsQuery({
    variables: {
      applicationsFacetsInput: {
        jobId: jobApplicationStore.jobId,
        filters: {
          // We are doing this because the UI allows for searching across all status folders at the moment.
          statusFolders: jobApplicationStore.lastResultSearchText
            ? []
            : ([
                jobApplicationStore.bucket,
              ] as unknown as ApplicationStatusFolder[]),
          searchText: jobApplicationStore.lastResultSearchText,
          homeLocationIds: filters.selectedHomeLocations.map(({ id }) => id),
          questionnaireFilters: jobApplicationStore.filtersStore
            .selectedRrFilters as unknown as QuestionnaireFilter[],
          nationalityRightToWorkIds:
            filters.searchFilters.nationalityRightToWorkIds,
        },
      },
      descriptionInput: {
        displayCountry: jobPostingCountry.toUpperCase(),
        language,
      },
      displayDescriptionInput: {
        language,
      },
      candidateProfileNationalityInput: {
        language,
      },
      canShowNationalityFilter,
    },
    fetchPolicy: 'no-cache',
  });

  const { handleStatusChange } = useHandleStatusChange({
    filters: filters.searchFilters,
    filtersEventCaptureData: filters.filtersEventCaptureData,
    addCustomError: dataDogRum.addCustomError,
  });

  const applicationFacetsDataToUse = applicationFacetsResult.data
    ? applicationFacetsResult.data
    : applicationFacetsResult.previousData;

  const isApplicationFacetsLoading =
    applicationFacetsResult.loading && !applicationFacetsResult.previousData;

  const renderLocationFilter = ({ mobile }: { mobile: boolean }) => (
    <FilterLocation
      hideLabel={mobile}
      dropdownBoxProps={{
        zIndex: mobile ? undefined : 'dropdown',
        boxShadow: mobile ? undefined : 'medium',
        position: mobile ? 'relative' : 'absolute',
      }}
      locations={
        applicationFacetsDataToUse?.applicationsFacets.__typename ===
        'ApplicationsFacetsResponseSuccess'
          ? applicationFacetsDataToUse.applicationsFacets.result
              .homeLocationFacets
          : []
      }
      loading={isApplicationFacetsLoading}
      jobPostingCountry={jobPostingCountry}
      selectedHomeLocations={filters.selectedHomeLocations}
      setSelectedHomeLocations={({ homeLocations, changes }) => {
        jobApplicationStore.clearAllSelected();
        changes.forEach((change) => {
          pageAnalytics.trackLink({
            event: 'searched_location_pressed',
            attributes: {
              searchLocationId: homeLocations.map(({ id }) => id),
              locationIdSelected: change.item.id,
              filterSelection: change.type,
            },
          });
        });
        useFiltersFns.setSelectedHomeLocations(homeLocations);
        jobApplicationStore.loadJobApplications({
          filters: {
            rrGlobalFilters: jobApplicationStore.filtersStore.selectedRrFilters,
            homeLocationIds: homeLocations.map(({ id }) => id),
            nationalityRightToWorkIds:
              filters.searchFilters.nationalityRightToWorkIds,
          },
          language,
          addCustomError: dataDogRum.addCustomError,
        });
      }}
    />
  );

  const renderNationalityFilter = ({ mobile }: { mobile: boolean }) => (
    <FilterNationality
      hideLabel={mobile}
      dropdownBoxProps={{
        zIndex: mobile ? undefined : 'dropdown',
        boxShadow: mobile ? undefined : 'medium',
        position: mobile ? 'relative' : 'absolute',
      }}
      nationalityFacets={
        applicationFacetsDataToUse?.applicationsFacets.__typename ===
        'ApplicationsFacetsResponseSuccess'
          ? applicationFacetsDataToUse.applicationsFacets.result.nationalityFacets?.map(
              (facet) => ({
                id: facet.nationality.id,
                name: facet.nationality.countryName,
                count: facet.count,
              }),
            ) ?? []
          : []
      }
      loading={isApplicationFacetsLoading}
      selectedNationalityFacets={filters.selectedNationalities}
      setSelectedNationalityFacets={({
        selectedNationalityFacets,
        changes,
      }) => {
        jobApplicationStore.clearAllSelected();
        changes.forEach((change) => {
          pageAnalytics.trackLink({
            event: 'searched_nationality_pressed',
            attributes: {
              searchNationalityId: selectedNationalityFacets.map(
                ({ id }) => id,
              ),
              nationalityIdSelected: change.item.id,
              filterSelection: change.type,
            },
          });
        });
        useFiltersFns.setSelectedNationalities(selectedNationalityFacets);
        jobApplicationStore.loadJobApplications({
          filters: {
            rrGlobalFilters: jobApplicationStore.filtersStore.selectedRrFilters,
            homeLocationIds: filters.searchFilters.homeLocationIds,
            nationalityRightToWorkIds: selectedNationalityFacets.map(
              ({ id }) => id,
            ),
          },
          language,
          experiments: trackingExperiments,
          addCustomError: dataDogRum.addCustomError,
        });
      }}
    />
  );

  const screeningQuestionFilters = (
    <FilterScreeningQuestion
      questionnaire={questionStore}
      isMustHaveToggleSelected={isMustHaveToggleSelected}
      questions={questions}
      onMustHaveFilterToggled={useFiltersFns.onMustHaveFilterToggled}
      onParentFilterSelected={useFiltersFns.onParentFilterSelected}
      onFilterSelected={useFiltersFns.onFilterSelected}
    />
  );

  const shouldShowClearFilterButton = filters.selectedFilterCount > 0;
  const onClickStatusFolder = (bucket: BUCKETS) => {
    if (jobApplicationStore.bucket !== bucket) {
      jobApplicationStore.clearAllSelected();
    }
    setQueryParams.navigateToBucket(bucket);
  };

  // Adding all filter buttons here
  const renderStatusFolderButtons = () => {
    const bucketDetails = jobApplicationStore.bucketStatsStore.bucketInfo;
    const bucket = jobApplicationStore.bucket;

    const statusFolderButtonsList = bucketDetails.map(
      (bucketInfo: BucketInfo) => (
        <StatusFolderButton
          key={bucketInfo.type}
          onClick={() => onClickStatusFolder(bucketInfo.type)}
          count={bucketInfo.count}
          newCount={bucketInfo.newCount}
          isSelected={
            bucketInfo.type === bucket && jobApplicationStore.isInBucketMode
          }
          type={bucketInfo.type}
        />
      ),
    );

    return (
      <Stack space="xxsmall">
        <>{statusFolderButtonsList}</>
      </Stack>
    );
  };

  const filterTabletDesktopContentLayout = (
    <FilterTabletDesktopContentLayout
      locationFilter={
        canShowLocationFilter ? renderLocationFilter({ mobile: false }) : null
      }
      nationalityFilter={
        canShowNationalityFilter
          ? renderNationalityFilter({ mobile: false })
          : null
      }
      screeningQuestionFilters={screeningQuestionFilters}
      statusFolders={renderStatusFolderButtons()}
    />
  );

  const homeLocationIdsForTracking =
    filters.searchFilters.homeLocationIds || [];
  const nationalityIdsForTracking =
    filters.searchFilters.nationalityRightToWorkIds || [];

  const urgentHireModal = (
    <FreemiumUrgentHireModal
      key="freemium-urgent-hire"
      link={router.upgradeJob({
        jobId: jobApplicationStore.jobId,
        ref: 'pop-up-urgent-hiring-candidate-management',
      })}
      jobCreatedDate={jobStore.job.jobInformation.jobCreatedDate}
      trackButtonPressed={() =>
        pageAnalytics.trackLink({
          event: 'candidate_management_freemium_pop_up_upgrade_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Urgent Hiring',
          },
        })
      }
      trackModalDisplayed={() =>
        pageAnalytics.trackView({
          event: 'candidate_management_freemium_pop_up_displayed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Urgent Hiring',
          },
        })
      }
      trackModalDismissed={() =>
        pageAnalytics.trackLink({
          event: 'alert_dismiss_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Urgent Hiring',
          },
        })
      }
    />
  );

  const hardToFillModal = (
    <FreemiumHardToFillModal
      key="freemium-hard-to-fill"
      link={router.upgradeJob({
        jobId: jobApplicationStore.jobId,
        ref: 'pop-up-hard-to-fill-candidate-management',
      })}
      postingLocationCountry={jobPostingCountry}
      jobCreatedDate={jobStore.job.jobInformation.jobCreatedDate}
      numberOfApplications={jobApplicationStore.bucketStats.totalCount}
      trackButtonPressed={() =>
        pageAnalytics.trackLink({
          event: 'candidate_management_freemium_pop_up_upgrade_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Hard to Fill',
          },
        })
      }
      trackModalDisplayed={() =>
        pageAnalytics.trackView({
          event: 'candidate_management_freemium_pop_up_displayed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Hard to Fill',
          },
        })
      }
      trackModalDismissed={() =>
        pageAnalytics.trackLink({
          event: 'alert_dismiss_pressed',
          attributes: {
            seekListingId: jobId,
            alertShownMessageCode:
              'Candidate Management - Freemium Upgrade - Hard to Fill',
          },
        })
      }
    />
  );

  const renderPageContent = () => {
    if (selectedPageTab === 'applications') {
      return (
        <AppErrorBoundary>
          <Box
            data-cy="main-content"
            paddingY="small"
            style={{ backgroundColor: '#F7F8FB' }} // FIXME: based on Braid, the value should be `neutralSoft` but browser displayed a different value
          >
            {shouldShowFreemium ? [urgentHireModal, hardToFillModal] : null}
            <Box display="flex">
              <Display desktop tablet>
                <FilterDesktop
                  shouldShowClearFilterButton={shouldShowClearFilterButton}
                  content={filterTabletDesktopContentLayout}
                  resetFilters={useFiltersFns.resetFilters}
                  selectedFilterCount={filters.selectedFilterCount}
                  isFilterPanelCollapsed={
                    getFilterDrawerState() === 'collapsed'
                  }
                  toggleFilterPanelCollapsed={toggleFilterDrawerState}
                />
              </Display>
              <Box flexGrow={1}>
                <CandidateList
                  addCustomError={dataDogRum.addCustomError}
                  actionToolbar={
                    isMobile ? (
                      <SearchResultSummary
                        bucket={jobApplicationStore.bucket}
                        hasSearchText={jobApplicationStore.hasSearchText}
                        matchingApplicationsCount={
                          jobApplicationStore.currentTotalOfJobApplications
                        }
                        selectedFilterCount={filters.selectedFilterCount}
                      />
                    ) : (
                      <ActionToolbar
                        selectedFilterCount={filters.selectedFilterCount}
                        bucket={jobApplicationStore.bucket}
                        currentTotalOfJobApplications={
                          jobApplicationStore.currentTotalOfJobApplications
                        }
                        selectedCount={jobApplicationStore.selectedCount}
                        sortBy={jobApplicationStore.sortBy}
                        hasSearchText={jobApplicationStore.hasSearchText}
                        hasQuestion={Boolean(
                          jobStore.questionnaire?.hasQuestion,
                        )}
                        onSortByChange={onSortByChange}
                        jobTitle={jobStore.job.jobInformation.title}
                        sendProspectEmail={
                          jobApplicationStore.sendProspectEmail
                        }
                        advertiserName={userStore.user.advertiserName}
                        userEmail={userStore.user.email}
                        userName={userStore.user.name}
                        isPageTabsSticky={isStickyOnDesktop}
                        selectAllStatus={jobApplicationStore.selectAllStatus}
                        matchingApplicationsCount={
                          jobApplicationStore.currentTotalOfJobApplications
                        }
                        selectAll={({
                          isCheckboxChecked,
                          clickSourceForTracking,
                        }) => {
                          if (isCheckboxChecked) {
                            jobApplicationStore.clearAllSelected();
                            if (clickSourceForTracking === 'checkbox') {
                              pageAnalytics.trackLink({
                                event: 'bulk_action_clear_selected_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  actionOrigin:
                                    'Candidate Listing - Bulk Select Check Box',
                                  bulkActionCount: 0,
                                },
                              });
                            } else if (clickSourceForTracking === 'menuitem') {
                              pageAnalytics.trackLink({
                                event: 'candidate_list_action_pressed',
                                attributes: {
                                  linkAction: 'select all on page',
                                  seekListingId: jobId,
                                  bulkActionCount: 0,
                                },
                              });
                            }
                          } else {
                            jobApplicationStore.clickSelectAllOnCurrentPage();
                            if (clickSourceForTracking === 'checkbox') {
                              pageAnalytics.trackLink({
                                event: 'bulk_actions_select_all_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  objectInteraction: 'select-all',
                                  bulkActionCount:
                                    jobApplicationStore
                                      .currentPageJobApplications.length,
                                },
                              });
                            } else if (clickSourceForTracking === 'menuitem') {
                              pageAnalytics.trackLink({
                                event: 'candidate_list_action_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  linkAction: 'select all on page',
                                  bulkActionCount:
                                    jobApplicationStore
                                      .currentPageJobApplications.length,
                                },
                              });
                            }
                          }
                        }}
                        deselectAll={() => {
                          jobApplicationStore.clearAllSelected();
                          pageAnalytics.trackLink({
                            event: 'candidate_list_action_pressed',
                            attributes: {
                              seekListingId: jobId,
                              linkAction: 'deselect all',
                              bulkActionCount: 0,
                            },
                          });
                        }}
                        onExpandApplicationCardState={() => {
                          trackCandidateCardStatus({
                            candidateCardStatus: 'Expand',
                            actionOrigin: 'Candidate Listing - Listing Change',
                          });
                        }}
                        onCollapseApplicationCardState={() => {
                          trackCandidateCardStatus({
                            candidateCardStatus: 'Collapse',
                            actionOrigin: 'Candidate Listing - Listing Change',
                          });
                        }}
                        selectAllJobApplicationsInBucket={({
                          shouldShowLoading,
                          onCompleted,
                        }) => {
                          jobApplicationStore.loadAllJobApplications({
                            addCustomError: dataDogRum.addCustomError,
                            selectAll: true,
                            shouldShowLoading,
                            onCompleted: () => {
                              pageAnalytics.trackLink({
                                event: 'candidate_list_action_pressed',
                                attributes: {
                                  seekListingId: jobId,
                                  linkAction: 'select all in folder',
                                  bulkActionCount:
                                    jobApplicationStore.selectedCount,
                                },
                              });
                              onCompleted();
                            },
                            filters: filters.searchFilters,
                          });
                        }}
                        onOpenBulkEmailDialog={() => {
                          trackBulkActionsEmailTabClicked(
                            jobApplicationStore.bulkActionEventAttributes,
                          );
                          sendHotjarTag(HOTJAR_TAG_BULK_ACTIONS);
                        }}
                        onOpenBulkMoveDialog={() => {
                          trackBulkActionsMoveTabClicked(
                            jobApplicationStore.bulkActionEventAttributes,
                          );
                          sendHotjarTag(HOTJAR_TAG_BULK_ACTIONS);

                          if (
                            jobApplicationStore.selectedJobApplicationIds
                              .length > BULK_MOVE_LIMIT
                          ) {
                            jobApplicationStore.selectedJobApplications
                              .slice(
                                BULK_MOVE_LIMIT,
                                jobApplicationStore.selectedJobApplications
                                  .length,
                              )
                              .forEach((jobApplication) =>
                                jobApplication.updateSelected(false),
                              );
                            showToast({
                              message: t(
                                'You can move up to bulkMoveLimit applications at a time',
                                { bulkMoveLimit: BULK_MOVE_LIMIT },
                              ),
                              description: t(
                                'We have selected the first bulkMoveLimit in this folder.',
                                { bulkMoveLimit: BULK_MOVE_LIMIT },
                              ),
                              tone: 'critical',
                            });
                            return false;
                          }
                          return true;
                        }}
                        onClickSelectionMenu={() => {
                          pageAnalytics.trackLink({
                            event: 'candidate_list_action_menu_pressed',
                            attributes: {
                              seekListingId: jobId,
                            },
                          });
                        }}
                        updateStatusForSelectedApplications={(newBucket) => {
                          trackUpdateStatusMultiple({
                            objectInteraction:
                              jobApplicationStore.selectAllStatus,
                            bulkActionCount:
                              jobApplicationStore
                                .selectedJobApplicationUpToBulkMoveLimit.length,
                            applicationStatus: getDashisedBucket(newBucket), // new bucket/status
                            bucket: getDashisedBucket(
                              jobApplicationStore.bucket,
                            ), // old bucket/status
                            candidateCardStatus:
                              jobApplicationStore.rootStore.getCandidateCardStateForTracking(),
                            searchLocationId: homeLocationIdsForTracking,
                            searchNationalityId: nationalityIdsForTracking,
                          });

                          return jobApplicationStore.updateStatusForSelectedApplications(
                            {
                              bucket: newBucket,
                              store: jobApplicationStore.rootStore,
                              reloadJobApplications: async () => {
                                await jobApplicationStore.loadJobApplications({
                                  language,
                                  filters: filters.searchFilters,
                                  experiments: trackingExperiments,
                                  addCustomError: dataDogRum.addCustomError,
                                });
                              },
                              filtersEventCaptureData:
                                filters.filtersEventCaptureData,
                              addCustomError: dataDogRum.addCustomError,
                            },
                          );
                        }}
                        // We are using a function here because accessing the mobx array directly causes rerenders. See PR #3167
                        getSelectedJobApplicationNames={() =>
                          jobApplicationStore.selectedJobApplicationUpToBulkMoveLimit.map(
                            ({ fullName }) => fullName,
                          )
                        }
                        onClickViewBulkMoveJobApps={(newStatus) => {
                          jobApplicationStore.clickViewMovedJobApplications();
                          setQueryParams.navigateToBucket(newStatus);
                        }}
                        addCustomError={dataDogRum.addCustomError}
                      />
                    )
                  }
                  reloadJobApplicationsWithLastResultSearchText={() => {
                    jobApplicationStore.loadJobApplications({
                      searchText: jobApplicationStore.lastResultSearchText,
                      language,
                      filters: filters.searchFilters,
                      experiments: trackingExperiments,
                      addCustomError: dataDogRum.addCustomError,
                    });
                  }}
                  clearTextAndReloadJobApplications={() => {
                    jobApplicationStore.updateSearchText('');
                    // FIXME: This is not updating the query param for search term, which is a bug
                    jobApplicationStore.loadJobApplications({
                      language,
                      searchText: jobApplicationStore.searchText,
                      filters: filters.searchFilters,
                      experiments: trackingExperiments,
                      addCustomError: dataDogRum.addCustomError,
                    });
                  }}
                  openRelevantApplicationDialog={
                    openRelevantApplicationAlertFromBanner
                  }
                  useFiltersFns={useFiltersFns}
                  isMustHaveToggleSelected={isMustHaveToggleSelected}
                  filtersEventCaptureData={filters.filtersEventCaptureData}
                  handleStatusChange={handleStatusChange}
                  refetchFacets={async () => {
                    // NOTE: Status update is async so we need to wait a bit after the mutation has returned to refetch or else we'd get the same results.
                    // This also solves the issue where
                    // 1. AppPageBlock is updated by MobX's `observer` function
                    // 2. Route is updated by changing of search params (inside onUpdateSuccess)
                    //
                    // (1.) runs first, and when it does, it still has the old query params with `&selected=<id>` which causes
                    // the profile drawer to re-open when `store.jobApplicationStore.updateFromQuery` is called in AppPageBlock
                    // Because of this, the drawer re-opens after it has been closed
                    // Reported: https://seekchat.slack.com/archives/C24PK4XV3/p1719356837551939
                    await sleep(1500);
                    await applicationFacetsResult.refetch();
                  }}
                  homeLocationIdsForTracking={homeLocationIdsForTracking}
                  nationalityIdsForTracking={nationalityIdsForTracking}
                  jobPlacementAlert={
                    // FIXME: the userStore.user.userId should be removed after we stop sending userId to graph
                    isJobPlacementShown.nudge && userStore.user.userId ? (
                      <JobPlacementAlert
                        openJobPlacementDialog={() =>
                          setIsJobPlacementShown((prev) => ({
                            ...prev,
                            dialog: true,
                          }))
                        }
                        jobTitle={jobStore.job.jobInformation.title}
                        jobLocations={jobStore.job.jobInformation.locations.join(
                          ', ',
                        )}
                        trackJobPlacementBannerDisplay={() => {
                          pageAnalytics.trackView({
                            event: 'candidate_management_banner_displayed',
                            attributes: {
                              seekListingId: jobId,
                              experiments: trackingExperiments,
                              displayedBanner:
                                'Candidate Management - Placement - Have you Placed a Candidate',
                            },
                          });
                        }}
                        trackBannerContentPressedEvent={() => {
                          pageAnalytics.trackLink({
                            event: 'banner_content_pressed',
                            attributes: {
                              seekListingId: jobId,
                              experiments: trackingExperiments,
                              bannerElement: 'Let us know',
                              displayedBanner:
                                'Candidate Management - Placement - Have you Placed a Candidate',
                            },
                          });
                        }}
                      />
                    ) : (
                      <></>
                    )
                  }
                />
              </Box>
            </Box>
          </Box>
        </AppErrorBoundary>
      );
    }
    if (selectedPageTab === 'notes') {
      return (
        <HirerJobActivity
          handleStatusChange={handleStatusChange}
          addCustomError={dataDogRum.addCustomError}
        />
      );
    }
  };

  const pageTabs = (
    <PageTabs
      isStickyOnDesktop={isStickyOnDesktop}
      selectedPageTab={selectedPageTab}
      setIsStickyOnDesktop={setIsStickyOnDesktop}
      setSelectedPageTab={setSelectedPageTab}
      showRecommendedCandidates={shouldShowRecommendedCandidates}
      jobId={jobApplicationStore.jobId}
      onApplicationsTabClick={() =>
        jobApplicationStore.loadJobApplications({
          language,
          filters: filters.searchFilters,
          experiments: trackingExperiments,
          addCustomError: dataDogRum.addCustomError,
        })
      }
    />
  );

  const mobileFilterSection = (
    <MobileFilterSection
      sortBy={jobApplicationStore.sortBy}
      hasSearchText={jobApplicationStore.hasSearchText}
      hasQuestion={Boolean(jobStore.questionnaire?.hasQuestion)}
      onSortByChange={onSortByChange}
      selectedFilterCount={filters.selectedFilterCount}
      openFilterDrawer={() => setFilterDrawerOpen(true)}
    />
  );

  return (
    <>
      <IntercomConfiguration />

      <AppPageLayout
        mobileStickyHeader={
          isMobile && (
            <MobileStickyHeader
              isAtTopOfPage={isAtTopOfPage}
              setIsAtTopOfPage={setIsAtTopOfPage}
              employerHeader={employerHeader}
              pageTabs={shouldShowRecommendedCandidates && pageTabs}
              mobileFilterSection={mobileFilterSection}
            />
          )
        }
        jobSection={{
          boxShadow: hidePageTabs ? 'small' : undefined,
          element: (
            <JobSection
              job={jobStore.job}
              isRecommendedCandidatesTabShown={shouldShowRecommendedCandidates}
              openRelevantApplicationDialog={() => {
                trackClickBillableApplicantsDefinition({
                  actionOrigin:
                    'Candidate Listing - Billable Applicants Button',
                });
                setIsRelevantApplicationDialogOpen(true);
              }}
              freemiumButton={
                shouldShowFreemium ? (
                  <FreemiumButton
                    link={router.upgradeJob({
                      jobId: jobApplicationStore.jobId,
                      ref: 'candidate-listing-candidate-management',
                    })}
                    onFreemiumButtonClicked={() =>
                      trackFreemiumButtonPressedEvent()
                    }
                    onFreemiumButtonLoaded={() => {
                      trackFreemiumButtonDisplay({
                        hasJobUpgradeButton: true,
                      });
                    }}
                  />
                ) : null
              }
              addCustomError={dataDogRum.addCustomError}
            />
          ),
        }}
        pageTabs={!hidePageTabs && pageTabs}
        mobileFilterSection={mobileFilterSection}
        content={renderPageContent()}
      />
      {jobStore.job.jobInformation.billableApplicants && (
        <RelevantApplicationDialog
          isOpen={isRelevantApplicationDialogOpen}
          mandatoryQuestions={questionStore.getMandatoryQuestions()}
          onClose={() => setIsRelevantApplicationDialogOpen(false)}
          relevantApplicationStats={
            jobStore.job.jobInformation.billableApplicants
          }
        />
      )}
      {/* FIXME: the userStore.user.userId should be removed after we stop sending userId to graph  */}
      {isJobPlacementShown.dialog && userStore.user.userId && (
        <JobPlacementDialog
          isOpen={isJobPlacementShown.dialog}
          close={() =>
            setIsJobPlacementShown((prev) => ({
              ...prev,
              dialog: false,
            }))
          }
          closeNudge={() =>
            setIsJobPlacementShown((prev) => ({
              ...prev,
              nudge: false,
            }))
          }
          hideJobPlacementInLocalStorage={() =>
            hideJobPlacementExpiryTimestamp()
          }
          jobTitle={jobStore.job.jobInformation.title}
          jobLocations={jobStore.job.jobInformation.locations.join(', ')}
          jobId={jobApplicationStore.jobId}
          advertiserId={Number(seekAdvertiserId)}
          userId={userStore.user.userId}
          trackJobPlacementDataSubmissionEvent={(
            optionSelected: 'Yes' | 'No' | 'Not Yet',
          ) => {
            pageAnalytics.trackLink({
              event: 'option_selection_pressed',
              attributes: {
                seekListingId: jobId,
                experiments: trackingExperiments,
                dialogName:
                  'Candidate Management - Placement - Have you Placed',
                optionSelected,
                optionSelectionType: 'radio',
                prospectInboxCount:
                  jobApplicationStore.bucketStats.getBucketCount(BUCKETS.INBOX),
                prospectPrescreenCount:
                  jobApplicationStore.bucketStats.getBucketCount(
                    BUCKETS.PRESCREEN,
                  ),
                prospectShortlistCount:
                  jobApplicationStore.bucketStats.getBucketCount(
                    BUCKETS.SHORTLIST,
                  ),
                prospectInterviewCount:
                  jobApplicationStore.bucketStats.getBucketCount(
                    BUCKETS.INTERVIEW,
                  ),
                prospectOfferCount:
                  jobApplicationStore.bucketStats.getBucketCount(BUCKETS.OFFER),
                prospectAcceptCount:
                  jobApplicationStore.bucketStats.getBucketCount(
                    BUCKETS.ACCEPT,
                  ),
                prospectNotSuitableCount:
                  jobApplicationStore.bucketStats.getBucketCount(
                    BUCKETS.NOT_SUITABLE,
                  ),
              },
            });
          }}
          trackJobPlacementDialogDismissedEvent={(
            dialogName:
              | 'Candidate Management - Placement - Have you Placed'
              | 'Candidate Management - Placement - Improve Job Ad'
              | 'Candidate Management - Placement - Thanks for Helping',
          ) => {
            pageAnalytics.trackLink({
              event: 'dialog_dismiss_pressed',
              attributes: {
                seekListingId: jobId,
                experiments: trackingExperiments,
                dialogName,
              },
            });
          }}
          trackJobPlacementDialogDisplay={(
            dialogName:
              | 'Candidate Management - Placement - Have you Placed'
              | 'Candidate Management - Placement - Improve Job Ad'
              | 'Candidate Management - Placement - Thanks for Helping',
          ) => {
            pageAnalytics.trackView({
              event: 'dialog_displayed',
              attributes: {
                seekListingId: jobId,
                experiments: trackingExperiments,
                dialogName,
              },
            });
          }}
          trackJobPlacementViewReportButtonPressedEvent={() => {
            pageAnalytics.trackLink({
              event: 'view_report_pressed',
              attributes: {
                seekListingId: jobId,
                experiments: trackingExperiments,
                linkAction: 'Redirect to Report',
                linkText: 'View report',
                dialogName: 'Candidate Management - Placement - Improve Job Ad',
              },
            });
          }}
        />
      )}

      <NotificationPreferenceDialog
        open={queryParams.overlay === 'user-preferences'}
        onClose={() => {
          setQueryParams.clearOverlayModal();
        }}
        jobId={String(jobId)}
        jobTitle={jobStore.job.jobInformation.title}
        isJobExpired={jobStore.job.isExpired}
        seekAdvertiserId={seekAdvertiserId}
        userId={String(userStore.user.userId)}
        email={userStore.user.email}
      />

      {isMobile ? (
        <FilterDrawerMobile
          showDrawer={isFilterDrawerOpen}
          setShowDrawer={setFilterDrawerOpen}
          statusFilters={{
            statusFolders: jobApplicationStore.bucketStatsStore.bucketInfo.map(
              (bucketInfo) => ({
                ...bucketInfo,
                selected:
                  bucketInfo.type === jobApplicationStore.bucket &&
                  !jobApplicationStore.lastResultSearchText,
              }),
            ),
            onClickStatusFolder,
          }}
          screeningQuestionFilters={
            questionStore.hasQuestion
              ? {
                  content: screeningQuestionFilters,
                  selectedQuestions:
                    jobApplicationStore.filtersStore.selectedQuestionAnswers.map(
                      ({ question }) => question.statement ?? question.text,
                    ),
                }
              : null
          }
          shouldShowClearFilterButton={shouldShowClearFilterButton}
          useFiltersFns={useFiltersFns}
          locationFilter={
            canShowLocationFilter
              ? {
                  content: renderLocationFilter({
                    mobile: true,
                  }),
                  selectedLocations: filters.selectedHomeLocations.map(
                    ({ name }) => name,
                  ),
                }
              : null
          }
          nationalityFilter={
            canShowNationalityFilter
              ? {
                  content: renderNationalityFilter({
                    mobile: true,
                  }),
                  selectedNationalities: filters.selectedNationalities.map(
                    ({ name }) => name,
                  ),
                }
              : null
          }
        />
      ) : null}
    </>
  );
});

const PageBlock: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <Box
    className={pageBlockStyles.pageBlock}
    display="flex"
    flexDirection="column"
    paddingY="none"
    paddingX={['none', 'xsmall']}
    marginY="none"
  >
    {children}
  </Box>
);
